/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styles from "./styles.module.scss";
import { ChevronRightIcon, CloseCircleGreyIcon, CopyIcon, CopyLinkIcon, MessagesIcon, ShareIcon, TickIcon, emptyReport2 } from 'assets'
import { BasicExpectationsTable, Breadcrumbs, Button, CandidateSelectionModal, CollapsibleSection, DoughnutChart, DropDown, Modal, PersonalityAlignmentBadge, PersonalityAlignmentChart, RadioInput, SkillsComparisonChart, StackDropDown, SummaryTable, Loader, Spinner } from "components";
import { ShareReportModal } from 'components/modal-variants/share-report-modal';
import { getScoreColor, getScoreBgClass, getScoreTextClass, getBadgeBgClass } from 'utils/scoreColor';
import { formatSalaryExpectation, formatSalaryRange } from 'utils/salary';
import { formatPersonName } from 'utils/titleCase';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useReportDetails,
  useReportSummary,
  useUpdateCandidateDecision,
  useBasicExpectation,
  useSoftSkills,
  useWorkStyle,
  useWorkplaceQualities,
  useWorkMotivator,
  usePersonality,
  useDomainKnowledge,
  useReviewTalentsCandidates,
  useFetchDecisionBuckets,
  useCandidateRetake,
  useAssessmentSections,
  isTabEnabled,
  matchCategoryToSectionKey,
  isSectionEnabled,
} from 'hooks';
// Mock data imports removed - using only real API data
import { useCredits } from 'hooks/useCredits';
import { CommentsModal } from 'components/modal-variants/comments-modal';
import { UnlockFullReportsModal } from 'pages/pricing-v2/components/unlock-full-reports-modal';
import { BuyAdditionalCreditModal } from 'pages/pricing-v2/components/buy-additional-credit-modal';
import { InsufficientCreditsModal } from 'components/modal-variants/credit-gate-modals';
import { XIcon } from 'lucide-react';

const schema = yup.object().shape({
  decision: yup.string().required('Please select a decision'),
});

type SuggestionVisualState = 'positive' | 'negative' | 'unknown';

const getSuggestionVisualState = (action?: string | null, status?: boolean | string | null): SuggestionVisualState => {
  const normalizedAction = action?.trim().toLowerCase();
  const normalizedStatus = typeof status === 'boolean'
    ? status
    : typeof status === 'string'
      ? status.trim().toLowerCase() === 'true'
      : null;

  if (normalizedAction === 'interview' && normalizedStatus === true) {
    return 'positive';
  }

  if (normalizedAction === 'reject' && normalizedStatus === false) {
    return 'negative';
  }

  return 'unknown';
};

const SuggestionStatusIcon: React.FC<{ state: SuggestionVisualState }> = ({ state }) => {
  if (state === 'positive') {
    return (
      <div className="w-6 h-6 rounded-[6px] bg-[#069952] flex items-center justify-center flex-shrink-0">
        <TickIcon className="w-[18px] h-[18px] [&>path]:stroke-white [&>path]:fill-none [&>path]:stroke-[5px]" />
      </div>
    );
  }

  if (state === 'negative') {
    return (
      <div className="w-6 h-6 rounded-[6px] bg-[#E74444] flex items-center justify-center flex-shrink-0">
        <XIcon className="w-4 h-[18px] [&>path]:stroke-white [&>path]:stroke-[3px]" />
      </div>
    );
  }

  return (
    <div className="w-6 h-6 rounded-[6px] bg-[#98A2B3] text-white flex items-center justify-center text-[16px] leading-none font-medium flex-shrink-0">
      <p className='-mt-1'>-</p>
    </div>
  );
};

const AssessmentReportUI = () => {
  // Get reportId from URL params
  const { id: assessmentId, id2: reportId } = useParams<{ id: string; id2: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch V2 API data
  const { reportDetails, isLoading: detailsLoading, error: reportError } = useReportDetails(reportId);

  // Fetch decision buckets for the assessment
  const { data: decisionBucketsData } = useFetchDecisionBuckets(assessmentId);
  const decisionBuckets = decisionBucketsData?.decision_buckets || [];

  // Return URL to the review-talents page with its exact filter/page/sort state preserved.
  // Carried in the `retReview` query param (set when the report was opened); falls back to the
  // bare review-talents URL when absent. Used by every "back to candidates" path on this page so
  // filters survive even though the report opens in a fresh tab with no browser history.
  const retReviewParam = new URLSearchParams(location.search).get('retReview');
  const reviewTalentsBackUrl = retReviewParam || `/employer/manage-jobs/review-talent/${assessmentId}`;

  // Redirect to review-talents page if assessment is locked
  useEffect(() => {
    if (reportError) {
      const errorMessage = (reportError as any)?.response?.data?.message;
      if (errorMessage === 'Assessment is locked' && assessmentId) {
        navigate(reviewTalentsBackUrl);
      }
    }
  }, [reportError, assessmentId, navigate, reviewTalentsBackUrl]);

  // Fetch all candidates for the assessment for pagination
  const { candidates: allCandidates, totalCount: candidateCount } = useReviewTalentsCandidates({
    assessmentId: assessmentId || '',
    decision: undefined,
    search: '',
    minScore: 0,
    maxScore: 100,
    sortBy: 'created_at',
    sortOrder: 'desc',
    page: 1,
    pageSize: 100, // Fetch enough candidates for pagination
  });
  const { summary, isLoading: summaryLoading } = useReportSummary(reportId);

  // Use individual hooks for each metric tab
  const { basicExpectation, isLoading: basicExpectationLoading } = useBasicExpectation(reportId);
  const { softSkills, isLoading: softSkillsLoading } = useSoftSkills(reportId);
  const { workStyle, isLoading: workStyleLoading } = useWorkStyle(reportId);
  const { workplaceQualities, isLoading: workplaceQualitiesLoading } = useWorkplaceQualities(reportId);
  const { workMotivator, isLoading: workMotivatorLoading } = useWorkMotivator(reportId);
  const { personality, isLoading: personalityLoading } = usePersonality(reportId);
  const { domainKnowledge, isLoading: domainKnowledgeLoading } = useDomainKnowledge(reportId);
  // Which metrics the recruiter kept in the hiring flow — deleted ones must not
  // render as report sections/tabs.
  const { sections: enabledSections } = useAssessmentSections(assessmentId);
  const suggestionVisualState = getSuggestionVisualState(summary?.suggestion?.action, summary?.suggestion?.status);

  // Hook for updating candidate decision
  const { updateDecision, isUpdating } = useUpdateCandidateDecision();

  // Handler for decision change
  const handleDecisionChange = (value: string) => {
    if (!reportDetails?.response_details?.id) return;

    // Map display decision to API decision value
    const decisionMap: Record<string, string> = {
      'Interview': 'interview',
      'Revisit': 'revisit',
      'Probe further': 'probe_further',
      'Request More Info': 'request_more_info',
      'Save for future': 'save_for_future',
      'Reject': 'Reject',
    };

    const apiDecision = decisionMap[value] || value.toLowerCase().replace(' ', '_');
    updateDecision({
      assessment_response_ids: [reportDetails.response_details.id],
      decision: apiDecision as any
    });
  };

  // Helper function to transform basic expectation data to table format
  const transformBasicExpectationToTable = () => {
    if (!basicExpectation || !basicExpectation.candidate_response || !basicExpectation.employer_expectation) {
      return [];
    }

    const { candidate_response, employer_expectation, comparisons } = basicExpectation;
    const shouldShowMetric = (flag?: boolean) => flag !== false;

    // Helper to format experience level
    const formatExperienceLevel = (level: string) => {
      if (!level) return 'N/A';
      return level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Helper to format years
    const formatYears = (years: string) => {
      if (!years) return 'N/A';
      return years.replace(/_/g, '-').replace(/\b\w/g, l => l.toUpperCase()) + ' years';
    };

    const tableItems: { label: string; expectation: string; response: string; result: string; }[] = [];

    if (shouldShowMetric(employer_expectation.require_experience_level)) {
      tableItems.push({
        label: 'Experience level',
        expectation: formatExperienceLevel(employer_expectation.experience_level),
        response: formatExperienceLevel(candidate_response.experience_level),
        result: comparisons?.experience_level?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_years_of_experience)) {
      tableItems.push({
        label: 'Years of experience',
        expectation: formatYears(employer_expectation.years_of_experience),
        response: formatYears(candidate_response.years_of_experience),
        result: comparisons?.years_of_experience?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_employment_type)) {
      tableItems.push({
        label: 'Employment Type',
        expectation: formatExperienceLevel(employer_expectation.employment_type),
        response: formatExperienceLevel(candidate_response.employment_type),
        result: comparisons?.employment_type?.result || 'conflict',
      });
    }

    if (employer_expectation.work_mode && shouldShowMetric(employer_expectation.require_work_mode)) {
      tableItems.push({
        label: 'Work Mode',
        expectation: formatExperienceLevel(employer_expectation.work_mode),
        response: formatExperienceLevel(candidate_response.work_mode || ''),
        result: comparisons?.work_mode?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_salary_range)) {
      tableItems.push({
        label: 'Salary Expectation',
        expectation: formatSalaryRange(employer_expectation),
        response: formatSalaryExpectation(candidate_response.salary_expectation, employer_expectation.salary_range_currency) || 'N/A',
        result: comparisons?.salary_expectation?.result || 'conflict',
      });
    }

    if (shouldShowMetric(employer_expectation.require_work_environment)) {
      tableItems.push({
        label: 'Experience environment',
        expectation: formatExperienceLevel(employer_expectation.experience_environment),
        response: formatExperienceLevel(candidate_response.experience_environment),
        result: comparisons?.experience_environment?.result || 'conflict',
      });
    }

    return tableItems;
  };

  // Helper function to transform soft skills data to chart format
  const transformSoftSkillsToChart = () => {
    if (!softSkills?.soft_skills || !Array.isArray(softSkills.soft_skills)) return [];

    return softSkills.soft_skills.map((skill) => ({
      name: skill?.name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
      expectation: skill?.score?.expectation || 0,
      result: skill?.score?.candidate_result || 0,
      resultLabel: skill?.score?.score_range ? skill.score.score_range.charAt(0).toUpperCase() + skill.score.score_range.slice(1) : 'N/A',
      color: getScoreColor(skill.score?.candidate_result || 0),
    }));
  };

  // Helper function to transform soft skills to analysis format
  const transformSoftSkillsToAnalysis = () => {
    if (!softSkills?.soft_skills || !Array.isArray(softSkills.soft_skills)) return [];

    return softSkills.soft_skills.map((skill) => ({
      title: skill?.name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
      score: skill?.score?.candidate_result || 0,
      level: skill?.score?.score_range ? skill.score.score_range.charAt(0).toUpperCase() + skill.score.score_range.slice(1) : 'N/A',
      sections: [
        {
          title: 'Implication',
          items: [skill?.score?.implication || 'N/A'],
        },
        {
          title: 'Impact on Work',
          items: [skill?.score?.impact_on_work || 'N/A'],
        },
      ].filter(section => section.items[0] !== 'N/A'),
    }));
  };

  // Helper function to transform work style data to chart format
  const transformWorkStyleToChart = () => {
    if (!workStyle) return [];

    const dimensions = [
      { key: 'prioritizing', name: 'Prioritizing' },
      { key: 'visualizing', name: 'Visualizing' },
      { key: 'planning', name: 'Planning' },
      { key: 'coordinating', name: 'Coordinating' },
    ];

    return dimensions
      .map((dim) => {
        const data = workStyle[dim.key as keyof typeof workStyle] as any;
        if (!data || typeof data !== 'object') return null;

        return {
          name: dim.name,
          expectation: data.expectation || 0,
          result: data.candidate_result || 0,
          resultLabel: data.score_range?.charAt(0).toUpperCase() + data.score_range?.slice(1) || 'N/A',
          color: getScoreColor(data.candidate_result || 0),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  };

  // Helper function to transform work style to analysis format
  const transformWorkStyleToAnalysis = () => {
    if (!workStyle) return [];

    const dimensions = [
      { key: 'prioritizing', name: 'Prioritizing' },
      { key: 'visualizing', name: 'Visualizing' },
      { key: 'planning', name: 'Planning' },
      { key: 'coordinating', name: 'Coordinating' },
    ];

    return dimensions
      .map((dim) => {
        const data = workStyle[dim.key as keyof typeof workStyle] as any;
        if (!data || typeof data !== 'object') return null;

        return {
          title: dim.name,
          score: data.candidate_result || 0,
          level: data.score_range?.charAt(0).toUpperCase() + data.score_range?.slice(1) || 'N/A',
          sections: [
            {
              title: 'Implication',
              items: [data.implication || 'N/A'],
            },
            {
              title: 'Impact on Work',
              items: [data.impact_on_work || 'N/A'],
            },
            {
              title: 'Opportunities for Growth',
              items: Array.isArray(data.opportunities_for_growth) ? data.opportunities_for_growth : [],
            },
          ].filter(section => section.items.length > 0),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  };

  // Helper function to transform workplace qualities to chart format
  const transformWorkplaceQualitiesToChart = () => {
    if (!workplaceQualities?.qualities || !Array.isArray(workplaceQualities.qualities)) return [];

    return workplaceQualities.qualities.map((quality) => ({
      name: quality?.title ? quality.title.charAt(0).toUpperCase() + quality.title.slice(1) : 'N/A',
      expectation: Array.isArray(workplaceQualities.employer_expectation) ? (workplaceQualities.employer_expectation.find(e => e?.title === quality?.title)?.score || 0) : 0,
      result: quality?.score || 0,
      resultLabel: quality?.score_range ? quality.score_range.charAt(0).toUpperCase() + quality.score_range.slice(1) : 'N/A',
      color: getScoreColor(quality?.score || 0),
    }));
  };

  // Helper function to transform workplace qualities to analysis format
  const transformWorkplaceQualitiesToAnalysis = () => {
    if (!workplaceQualities?.qualities || !Array.isArray(workplaceQualities.qualities)) return [];

    return workplaceQualities.qualities.map((quality) => ({
      title: quality?.title ? quality.title.charAt(0).toUpperCase() + quality.title.slice(1) : 'N/A',
      score: quality?.score || 0,
      level: quality?.score_range ? quality.score_range.charAt(0).toUpperCase() + quality.score_range.slice(1) : 'N/A',
      sections: [
        {
          title: 'What this means for you',
          inline: true,
          items: [quality?.what_this_tells_you].filter(Boolean),
        },
        {
          title: 'In the role, expect them to',
          inline: true,
          items: [quality?.in_the_role].filter(Boolean),
        },
        {
          title: 'Watch out for',
          inline: true,
          items: [quality?.watch_out_for].filter(Boolean),
        },
        {
          title: 'Best suited for roles requiring',
          inline: true,
          items: [quality?.works_best_in].filter(Boolean),
        },
      ].filter(section => section.items.length > 0),
    }));
  };

  // Helper function to transform work motivators to analysis format
  const transformWorkMotivatorsToAnalysis = () => {
    if (!workMotivator?.candidate_result || !Array.isArray(workMotivator.candidate_result)) return [];

    return workMotivator.candidate_result.map((result) => {
      const matchStatus = result?.match ? '✓ Matches Employer Expectation' : '✗ Does Not Match Employer Expectation';

      return {
        title: result?.details?.name || 'N/A',
        score: result?.details?.score || 0,
        level: `${result?.details?.score_range ? result.details.score_range.charAt(0).toUpperCase() + result.details.score_range.slice(1) : 'N/A'} - ${matchStatus}`,
        sections: [
          {
            title: 'What this tells you',
            inline: true,
            items: [result?.details?.what_this_tells_you].filter(Boolean),
          },
          {
            title: 'In the role, expect them to',
            inline: true,
            items: [result?.details?.in_the_role].filter(Boolean),
          },
          {
            title: 'Watch out for',
            inline: true,
            items: [result?.details?.watch_out_for].filter(Boolean),
          },
          {
            title: 'Works best in environments that',
            inline: true,
            items: [result?.details?.works_best_in].filter(Boolean),
          },
        ].filter(section => section.items.length > 0), // Remove empty sections
      };
    });
  };

  const transformOtherKeyMotivatorsToAnalysis = () => {
    const workMotivatorData = workMotivator as any;
    const otherKeyMotivators =
      workMotivatorData?.other_key_motivators ||
      workMotivatorData?.otherKeyMotivators ||
      workMotivatorData?.key_motivators ||
      workMotivatorData?.motivators;

    if (!otherKeyMotivators || !Array.isArray(otherKeyMotivators)) return [];

    return otherKeyMotivators.map((motivator: any) => {
      const details = motivator?.details || motivator || {};

      return {
        title: details?.name || motivator?.title || motivator?.name || 'N/A',
        score: details?.score || motivator?.score || 0,
        level: `${details?.score_range ? details.score_range.charAt(0).toUpperCase() + details.score_range.slice(1) : 'N/A'}`,
        sections: [
          {
            title: 'What this tells you',
            inline: true,
            items: [details?.what_this_tells_you].filter(Boolean),
          },
          {
            title: 'In the role, expect them to',
            inline: true,
            items: [details?.in_the_role].filter(Boolean),
          },
          {
            title: 'Watch out for',
            inline: true,
            items: [details?.watch_out_for].filter(Boolean),
          },
          {
            title: 'Works best in environments that',
            inline: true,
            items: [details?.works_best_in].filter(Boolean),
          },
        ].filter(section => section.items.length > 0),
      };
    });
  };

  // Helper function to transform employer expectations to analysis format
  const transformEmployerExpectationsToAnalysis = () => {
    if (!workMotivator?.expectations || !Array.isArray(workMotivator.expectations)) return [];

    return workMotivator.expectations.map((exp) => {
      const matchStatus = exp?.match ? '✓ Matches Candidate Result' : '✗ Does Not Match Candidate Result';

      return {
        title: exp?.details?.name || 'N/A',
        score: exp?.details?.score || 0,
        level: `${exp?.details?.score_range ? exp.details.score_range.charAt(0).toUpperCase() + exp.details.score_range.slice(1) : 'N/A'} - ${matchStatus}`,
        sections: [
          {
            title: 'What this tells you',
            inline: true,
            items: [exp?.details?.what_this_tells_you].filter(Boolean),
          },
          {
            title: 'In the role, expect them to',
            inline: true,
            items: [exp?.details?.in_the_role].filter(Boolean),
          },
          {
            title: 'Watch out for',
            inline: true,
            items: [exp?.details?.watch_out_for].filter(Boolean),
          },
          {
            title: 'Works best in environments that',
            inline: true,
            items: [exp?.details?.works_best_in].filter(Boolean),
          },
        ].filter(section => section.items.length > 0), // Remove empty sections
      };
    });
  };

  // Helper function to transform work motivators data to chart format
  const transformWorkMotivatorsToChart = () => {
    if (!workMotivator?.candidate_result || !Array.isArray(workMotivator.candidate_result)) return [];

    return workMotivator.candidate_result.map((result) => {
      const name = result?.details?.name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A';
      const candidateScore = result?.details?.score || 0;
      // Find matching employer expectation by name
      const matchingExpectation = workMotivator.expectations?.find(
        (exp) => exp?.details?.name === result?.details?.name
      );
      const expectationScore = matchingExpectation?.details?.score || 0;

      return {
        name,
        expectation: expectationScore,
        result: candidateScore,
        resultLabel: result?.details?.score_range
          ? result.details.score_range.charAt(0).toUpperCase() + result.details.score_range.slice(1)
          : 'N/A',
        color: getScoreColor(candidateScore),
      };
    });
  };

  // Helper function to transform personality core areas to analysis format
  const transformPersonalityToAnalysis = () => {
    if (!personality?.core_areas || !Array.isArray(personality.core_areas)) return [];

    const skills: {
      title: string;
      score: number;
      sections: { title: string; items: string[] }[];
    }[] = [];

    personality.core_areas.forEach((area) => {
      if (!area) return;

      // Use the dominant boundary (top_boundary) for the analysis card
      const boundary = area.top_boundary || area.bottom_boundary;
      if (boundary) {
        const sections: { title: string; items: string[] }[] = [];

        if (boundary.description) {
          sections.push({
            title: 'Description',
            items: [boundary.description],
          });
        }

        if (Array.isArray(boundary.implications) && boundary.implications.length > 0) {
          sections.push({
            title: 'Implication',
            items: boundary.implications,
          });
        }

        skills.push({
          title: boundary.name || area.title || 'N/A',
          score: boundary.score || 0,
          sections,
        });
      }
    });

    return skills;
  };

  // Credit gating — balance is the only thing that can block an action.
  const { credits: creditsSnapshot, balance: credits, getCostByKeyword, canAfford } = useCredits();
  const maxCredits = creditsSnapshot?.weekly.allowance ?? 0;
  const [showUnlockReportsModal, setShowUnlockReportsModal] = useState(false);
  const [showBuyCreditModal, setShowBuyCreditModal] = useState(false);
  const [showCompareCreditModal, setShowCompareCreditModal] = useState(false);

  const compareCreditCost = getCostByKeyword('compare', 8);
  const shareCreditCost = getCostByKeyword('share', 3);
  const collabCreditCost = getCostByKeyword('collab', 3);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [comments, setComments] = useState([
    {
      id: '1',
      author: 'Emmanuel Doe',
      content: 'We appreciate your interest in this position and would like to assess your abilities in alignment with our requirements.',
      timestamp: new Date(Date.now() - 5 * 60000).toISOString(), // 5 mins ago
    },
  ]);

  const handleSubmitComment = (content: string) => {
    const newComment = {
      id: Date.now().toString(),
      author: 'Jane Doe',
      content,
      timestamp: new Date().toISOString(),
    };
    setComments([newComment, ...comments]);
  };
  const [isOpen, setIsOpen] = useState(false);

  // Find current candidate index in the list
  const currentCandidateIndex = allCandidates.findIndex(
    candidate => candidate.id.toString() === reportId
  );
  const currentPage = currentCandidateIndex !== -1 ? currentCandidateIndex + 1 : 1;

  const goToPrevPage = () => {
    if (currentCandidateIndex > 0 && assessmentId) {
      const prevCandidate = allCandidates[currentCandidateIndex - 1];
      // Carry retReview/from forward so the back-to-candidates context survives prev/next.
      navigate(`/employer/manage-jobs/review-talent/${assessmentId}/report/${prevCandidate.id}${location.search}`);
    }
  };

  const goToNextPage = () => {
    if (currentCandidateIndex < allCandidates.length - 1 && assessmentId) {
      const nextCandidate = allCandidates[currentCandidateIndex + 1];
      navigate(`/employer/manage-jobs/review-talent/${assessmentId}/report/${nextCandidate.id}${location.search}`);
    }
  };

  const defaultBucket = reportDetails?.response_details?.bucket || '';

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { decision: defaultBucket },
  });

  // Update form value when reportDetails loads
  useEffect(() => {
    if (reportDetails?.response_details?.bucket) {
      methods.setValue('decision', reportDetails.response_details.bucket);
    }
  }, [reportDetails?.response_details?.bucket, methods]);

  const [showShareCreditModal, setShowShareCreditModal] = useState(false);
  const [showCollabCreditModal, setShowCollabCreditModal] = useState(false);
  const [showRetakeModal, setShowRetakeModal] = useState(false);
  const [retakeBannerDismissed, setRetakeBannerDismissed] = useState(false);
  const { mutate: requestCandidateRetake, isPending: isRetakeLoading } = useCandidateRetake();
  const handleAllowRetake = () => {
    const responseId = reportDetails?.response_details?.id;
    if (!responseId) return;
    requestCandidateRetake(
      { response_id: responseId },
      {
        onSuccess: () => {
          setShowRetakeModal(false);
          setRetakeBannerDismissed(true);
          if (assessmentId) {
            navigate(reviewTalentsBackUrl);
          }
        },
      }
    );
  };
  const basicExpectationTableItems = transformBasicExpectationToTable();
  const showCvRelevance = basicExpectation?.employer_expectation?.require_cv_upload !== false && !!basicExpectation?.cv_relevance;
  const workMotivatorsAnalysis = transformWorkMotivatorsToAnalysis();
  const otherKeyMotivatorsAnalysis = transformOtherKeyMotivatorsToAnalysis();
  const employerExpectationsAnalysis = transformEmployerExpectationsToAnalysis();

  const handleShareClick = () => {
    if (!canAfford(shareCreditCost)) {
      setShowShareCreditModal(true);
      return;
    }
    setIsShareModalOpen(true);
  };

  const handleCompareClick = () => {
    setShowCompareCreditModal(true);
  };

  const handleCompareConfirm = () => {
    setShowCompareCreditModal(false);
    setIsOpen(true);
  };

  const handleCommentClick = () => {
    if (!canAfford(collabCreditCost)) {
      setShowCollabCreditModal(true);
      return;
    }
    setIsCommentsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDone = (selectedCandidate: string) => {
    setIsOpen(false);
  };

  // Scores - Only use API data from individual hooks, no fallbacks
  const score = reportDetails?.response_details?.total_score ? parseFloat(reportDetails.response_details.total_score) : undefined;
  // Only treat a zero-score result as a retake-eligible disruption when the
  // candidate actually completed the assessment (not still in progress/abandoned).
  const isAssessmentDisrupted =
    score === 0 &&
    reportDetails?.response_details?.status === 'completed' &&
    !retakeBannerDismissed;
  const basicExpectationScore = basicExpectation?.score;
  const workStyleScore = workStyle?.score;
  const workplaceQualitiesScore = workplaceQualities?.score;
  const workMotivatorsScore = workMotivator?.score;
  const personalityScore = personality?.score;
  const domainKnowledgeScore = domainKnowledge?.percentage;

  // Get tab from URL query params or default to 'Summary'
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab') || 'Summary';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('tab', tab);
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  const tabs = [
    'Summary',
    'Basic Expectation',
    'Soft Skills',
    'Work Style',
    'Workplace Qualities',
    'Work Motivators',
    'Personality',
    'Domain Knowledge',
  ];

  // Drop tabs for metrics the recruiter deleted in the hiring flow. Until the
  // sections request resolves, isTabEnabled returns true so nothing is hidden.
  const visibleTabs = tabs.filter((tab) => isTabEnabled(enabledSections, tab));

  // If the URL points at a now-hidden tab, fall back to Summary so the content
  // area never renders blank.
  useEffect(() => {
    if (enabledSections && !visibleTabs.includes(activeTab)) {
      setActiveTab('Summary');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledSections, activeTab]);

  const DecisionsDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState(reportDetails?.response_details?.bucket || '');

    const selectedLabel = selectedDecision || 'Select';

    const handleMobileDecisionChange = (value: string) => {
      setSelectedDecision(value);
      handleDecisionChange(value);
      setIsOpen(false);
    };

    return (
      <div className="w-[86%] mx-auto mb-5">
        <div className="relative w-full mx-auto">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full bg-[#E6F2FF] border border-[#B8D4F0] rounded-lg px-4 py-3 flex items-center justify-between text-left font-medium hover:bg-[#E6F2FF] ${selectedDecision ? 'text-[#1a3c64]' : 'text-[#344054]'}`}
          >
            <span>{selectedLabel}</span>
            <ChevronRightIcon
              className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : '-rotate-90'} w-[7px] h-auto ml-[-2px]`}
              style={{ strokeWidth: '1px', stroke: '#403DAA' }}
            />
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E4E7EC] rounded-lg shadow-lg z-[9999] p-3 overflow-hidden"
              >
                <RadioInput
                  name="decision"
                  type="radio"
                  register={methods.register}
                  options={decisionBuckets.map(bucket => ({
                    label: bucket.decision,
                    value: bucket.decision,
                  }))}
                  validatorMessage={methods.formState.errors.decision?.message}
                  parentClassName="!mb-0 [&>label]:w-fit [&>label]:mb-[5px] [&>label]:mt-auto [&>label]:pt-[8px] [&>label]:text-[#344054] [&>label]:px-4 [&>label]:hover:bg-[#F9FAFB] [&>label]:transition-colors [&>label]:duration-150 [&>label]:cursor-pointer [&>label>div]:flex [&>label>div]:items-center [&>label>div>input]:mr-[7px] [&>label>div>input]:w-[18px] [&>label>div>input]:h-[18px] lg:[&>label>div>input]:w-[16px] lg:[&>label>div>input]:h-[16px] [&>label>div>input:checked::after]:w-[9px] [&>label>div>input:checked::after]:h-[9px]"
                  onChange={(e) => handleMobileDecisionChange(e.target.value)}
                />
                {isUpdating && <p className="text-sm text-gray-500 mt-2 px-4">Updating...</p>}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {isOpen && ( 
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>
    );
  };

  const ScoreBadge: React.FC<{ score: number; max?: number }> = ({ score, max = 10 }) => {
    const percentage = (score / max) * 100;
    return (
      <span className={`p-1 rounded text-white ${getScoreBgClass(percentage)}`}>
        {score}/{max}
      </span>
    );
  };

  const DomainKnowledgeCollapsible: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
      <div className="rounded-lg bg-[#FAFAFB] mb-4 last:mb-0">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex justify-between items-center p-4 bg-transparent border-0 rounded-lg cursor-pointer text-[14px] font-semibold text-[#344054] transition-colors hover:bg-[#F2F4F7]"
        >
          <span className="text-left">{title}</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          >
            <path d="M4 6L8 10L12 6" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {isOpen && (
          <div className="px-4 pb-4">
            <p className="text-[14px] text-[#475467] leading-relaxed m-0">{children}</p>
          </div>
        )}
      </div>
    );
  };

  const DomainKnowledgeSection = () => {
    // Show loading state
    if (domainKnowledgeLoading) {
      return (
        <div className="flex justify-center items-center py-20">
          <Spinner />
        </div>
      );
    }

    // Show empty state if no data
    const hasDomainKnowledgeData = domainKnowledge?.results && Array.isArray(domainKnowledge.results) && domainKnowledge.results.length > 0;

    if (!hasDomainKnowledgeData) {
      return (
        <div className="flex flex-col items-center justify-center py-[100px] px-5">
          <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
          <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Domain Knowledge Data</h3>
          <p className="text-sm text-[#667085] text-center max-w-md">
            Domain knowledge assessment data is not available for this candidate yet.
          </p>
        </div>
      );
    }

    // Render with API data
    return (
      <div className="px-5 mt-6">
        {domainKnowledge?.results?.map((result: any, index: number) => (
          <div key={index} className="mb-8 last:mb-0">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-base font-semibold text-[#101828]">
                {result?.question_content || 'N/A'}
              </span>
              <ScoreBadge score={result?.points_earned ?? result?.score ?? 0} max={result?.points_possible ?? 10} />
            </div>

            <DomainKnowledgeCollapsible title="Score reason:">
              {result?.reason || result?.score_reason || 'N/A'}
            </DomainKnowledgeCollapsible>

            <DomainKnowledgeCollapsible title="Candidates answer:">
              {result?.answer || 'N/A'}
            </DomainKnowledgeCollapsible>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <Loader loading={detailsLoading} />
      {!detailsLoading && (
        <>
          <UnlockFullReportsModal
            isOpen={showUnlockReportsModal}
            onClose={() => setShowUnlockReportsModal(false)}
            variant="candidates"
            onTopupCredit={() => {
              setShowUnlockReportsModal(false);
              setShowBuyCreditModal(true);
            }}
          />
          <BuyAdditionalCreditModal
            isOpen={showBuyCreditModal}
            onClose={() => setShowBuyCreditModal(false)}
            currentBalance={credits}
          />

          {/* Compare - credit confirmation */}
          {showCompareCreditModal && (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
              onClick={() => setShowCompareCreditModal(false)}
            >
              <div
                className="bg-white rounded-2xl p-8 max-w-[420px] w-[90%] text-center relative"
                style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowCompareCreditModal(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M15 9l-6 6M9 9l6 6" />
                  </svg>
                </button>
                <p className="text-sm text-[#475467] mb-1">Your Credit balance:</p>
                <p className="text-4xl font-bold text-[#101828] mb-4">{credits}</p>
                <div className="flex items-center gap-2 bg-[#F9FAFB] border border-[#EAECF0] rounded-lg px-4 py-3 mb-6">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475467" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p className="text-sm text-[#475467]">
                    Comparing candidates will cost you <b>{compareCreditCost}</b> credits
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCompareCreditModal(false)}
                    className="flex-1 py-3 border border-[#D0D5DD] text-[#344054] font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCompareConfirm}
                    disabled={credits < compareCreditCost}
                    className={`flex-1 py-3 font-medium rounded-lg transition-colors ${credits < compareCreditCost
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-[#012169] text-white hover:bg-[#011557]'
                      }`}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}

          <CandidateSelectionModal isOpen={isOpen} onClose={handleClose} onDone={handleDone} />
          <CommentsModal
            isOpen={isCommentsOpen}
            onClose={() => setIsCommentsOpen(false)}
            comments={comments}
            onSubmitComment={handleSubmitComment}
            currentUser="Jane Doe"
          />
          <ShareReportModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            reportId={reportId}
            assessmentId={assessmentId}
          />


          <InsufficientCreditsModal
            isOpen={showShareCreditModal}
            onClose={() => setShowShareCreditModal(false)}
            credits={credits}
            maxCredits={maxCredits}
            cost={shareCreditCost}
            actionLabel="Sharing this report"
            onTopUp={() => {
              setShowShareCreditModal(false);
              setShowBuyCreditModal(true);
            }}
          />


          <InsufficientCreditsModal
            isOpen={showCollabCreditModal}
            onClose={() => setShowCollabCreditModal(false)}
            credits={credits}
            maxCredits={maxCredits}
            cost={collabCreditCost}
            actionLabel="Starting a collaboration session"
            onTopUp={() => {
              setShowCollabCreditModal(false);
              setShowBuyCreditModal(true);
            }}
          />

          <section className={`lg:mt-[0] py-5 ${styles.mainSection}`}>
            <section className='px-4 lg:ml-5'>
              <Breadcrumbs />
              <div className={styles.headerSection}>
                <h3 className={`md:block hidden ${styles.headerText}`}>
                  {reportDetails?.assessment_summary?.title || 'Assessment'} Report
                </h3>
                <div className={`md:flex hidden ${styles.pagination}`}>
                  <div className={`${styles.candidates} cursor-pointer`} onClick={() => navigate(reviewTalentsBackUrl)}>
                    <div className={`${styles.status} ${reportDetails?.assessment_summary?.status === 'active' ? '' : styles.statusInactive}`}>
                      <div className={`${styles.dot} ${reportDetails?.assessment_summary?.status === 'active' ? '' : styles.dotInactive}`}></div>
                      {reportDetails?.assessment_summary?.status === 'active' ? 'Active' : 'Inactive'}
                    </div> <ChevronRightIcon className={styles.chevronRight} /> {candidateCount || 0} Candidate{candidateCount === 1 ? '' : 's'}
                  </div>
                  <div className={styles.buttons}>
                    <button
                      onClick={goToPrevPage}
                      disabled={currentCandidateIndex <= 0}
                      className='whitespace-nowrap flex gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <ChevronRightIcon className={styles.chevronLeft} /> Prev
                    </button>
                    <span>{currentPage} <span className={styles.pages}> of {candidateCount || 0} </span></span>
                    <button
                      onClick={goToNextPage}
                      disabled={currentCandidateIndex >= allCandidates.length - 1 || currentCandidateIndex === -1}
                      className='whitespace-nowrap flex gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      Next
                      <ChevronRightIcon className={`${styles.chevronRight} ${styles.chevronRightPagination}`} />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <div className='flex gap-[1.7%] mt-5 relative md:flex-row flex-col md:mx-[34px] mx-0'>
              {/* Candidate Details Sidebar */}
              <div className={`min-[1150px]:p-4 md:p-2 p-4 md:w-[23.7%] md:mx-0 mx-4 md:sticky grid md:grid-cols-1 sm:grid-cols-3 grid-cols-1 md:mb-0 mb-8 ${styles.candidateDetails}`}>
                <div data-tour-id="tour-report-score" className={styles.scoreWrap}>
                  {score !== undefined ? (
                    <>
                      <DoughnutChart
                        cutout='82%'
                        score={score}
                        className={styles.scoreChart}
                        borderRadius={[20, 0]}
                        total={100}
                      />
                      <p
                        className={`${styles.scoreCount} ${score >= 100 ? styles.threeDigit : ''} ${score >= 90
                          ? styles.green
                          : score >= 70
                            ? styles.blue
                            : score >= 50
                              ? styles.orange
                              : styles.red
                          }`}
                      >
                        {score}%
                      </p>
                      <p className={styles.avg}>avg.</p>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full text-center">
                      <p className="text-[#667085] text-sm">Score not available</p>
                    </div>
                  )}
                </div>

                <div className='text-center mx-auto mb-5 mt-0 w-full min-[1150px]:w-[84%]'>
                  <h2 className="text-[#1D2939] mb-[10px] font-medium sm:text-[19px] text-2xl">
                    {formatPersonName(reportDetails?.response_details?.full_name || '') || 'Candidate'}
                  </h2>
                  <p className='text-[#667085] my-2 mx-0 sm:text-sm text-base break-words'>{reportDetails?.response_details?.email || ''}</p>
                  <p className='text-[#667085] mx-0 text-sm'>{reportDetails?.response_details?.phone_number || ''} {reportDetails?.response_details?.phone_number && reportDetails?.response_details?.created_at ? '|' : ''} {reportDetails?.response_details?.created_at ? new Date(reportDetails.response_details.created_at).toLocaleDateString() : ''}</p>
                  <div className='flex justify-center gap-4 mx-0 min-[1150px]:mt-5 min-[1150px]:mb-6 my-3'>
                    {reportDetails?.response_details?.portfolio && (
                      <a href={reportDetails.response_details.portfolio} target="_blank" rel="noopener noreferrer" className="flex items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline">
                        <CopyLinkIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
                        Portfolio
                      </a>
                    )}
                    {reportDetails?.response_details?.resume && (
                      <a href={reportDetails.response_details.resume} target="_blank" rel="noopener noreferrer" className="flex items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline">
                        <CopyIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
                        CV
                      </a>
                    )}
                  </div>
                  <div className='border-b-[1.5px] border-[#EAECF0] xl:w-[200px] md:w-[80%] w-[200px] mx-auto ' />
                </div>

                {/* Desktop Decisions */}
                <div data-tour-id="tour-report-decisions" className="w-[86%] mx-auto md:block hidden">
                  <h3 className="text-[#1a3c64] mb-[10px] font-medium text-[16px] md:w-full sm:w-[60%] mx-auto">
                    Decisions
                  </h3>
                  <RadioInput
                    name="decision"
                    type="radio"
                    register={methods.register}
                    options={decisionBuckets.map(bucket => ({
                      label: bucket.decision,
                      value: bucket.decision,
                    }))}
                    validatorMessage={methods.formState.errors.decision?.message}
                    parentClassName={`!md:mb-9 !mb-0 pt-[5px] !md:w-full !sm:w-[60%] !mx-auto [&>label]:w-fit [&>label]:mb-[5px] [&>label]:mt-auto [&>label]:border-t [&>label]:border-[#F2F4F7] [&>label]:pt-[8px] [&>label]:text-[#344054] [&>label>div]:flex [&>label>div]:items-center [&>label>div>input]:mr-[7px] [&>label>div>input]:w-[18px] [&>label>div>input]:h-[18px] lg:[&>label>div>input]:w-[16px] lg:[&>label>div>input]:h-[16px] [&>label>div>input:checked::after]:w-[9px] [&>label>div>input:checked::after]:h-[9px] ${styles.radioButton}`}
                    onChange={(e) => handleDecisionChange(e.target.value)}
                  />
                  {isUpdating && <p className="text-sm text-gray-500 mt-2">Updating...</p>}
                </div>

                {/* Mobile Decisions */}
                <div className='md:hidden'>
                  <DecisionsDropdown />
                </div>

                {/* <div onClick={() => setIsCommentsOpen(true)} className="flex md:hidden mx-auto mt-2 items-center gap-[6px] text-[#175CD3] xl:text-[0.9rem] text-sm hover:underline">
                  <MessagesIcon className="w-5 [&>path]:fill-[#175CD3] [&>path]:stroke-[0.2]" />
                  Comments (0)
                </div> */}
              </div>

              {/* Main Content */}
              <div className={`md:w-[74.6%] w-full ${styles.candidateReport}`}>
                {isAssessmentDisrupted && (
                  <div className="flex items-center justify-between gap-4 flex-wrap bg-[#FFF5E6] border border-[#EDA12F] rounded-[10px] px-5 py-4 mb-4 mx-4 md:mx-0">
                    <div className="flex-1 min-w-[240px]">
                      <h4 className="text-base font-semibold text-[#1D2939] mb-1">Assessment attempt was disrupted</h4>
                      <p className="text-[13px] text-[#475467] leading-[1.5] m-0">
                        This candidate&apos;s previous attempt appears disrupted. They will receive a new
                        assessment link immediately.
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        type="outline"
                        onClick={() => setRetakeBannerDismissed(true)}
                        className="!border !border-[#98A2B3] !text-[#344054] !bg-transparent !px-4 !py-2 !h-fit !rounded-[5px] !text-sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="fill"
                        onClick={() => setShowRetakeModal(true)}
                        className="!bg-[#012169] !border-none !text-white !px-4 !py-2 !h-fit !rounded-[5px] !text-sm"
                      >
                        Allow Retake
                      </Button>
                    </div>
                  </div>
                )}
                {/* Action Buttons */}
                <div className='w-full overflow-auto scrollbar-none'>
                  <div className="!flex !justify-between md:mx-0 mx-4 min-w-[500px]">
                    <div className="!flex !sm:gap-[12px] gap-2">
                      <Button
                        type="outline"
                        onClick={handleShareClick}
                        className="!flex !items-center !gap-1 !sm:gap-[8px] !border !border-[#98A2B3] !text-[#344054] !bg-transparent !sm:px-[17px] !sm:py-[10px] !py-1.5 !px-2 !h-fit !rounded-[3px] !sm:text-[14px] !text-xs"
                      >
                        <ShareIcon className='sm:w-[18px] sm:h-[18px] w-4 h-4' /> Share Report
                      </Button>

                      <Button
                        type="outline"
                        onClick={handleCompareClick}
                        className="!flex !items-center !gap-1 !sm:gap-[8px] !border !border-[#012169] !text-[#012169] !bg-transparent !sm:px-[17px] !sm:py-[10px] !py-1.5 !px-2 !h-fit !rounded-[5px] !sm:text-[14px] !text-xs !hover:bg-transparent"
                        data-tour-id="tour-report-compare"
                      >
                        Compare Candidates
                      </Button>
                    </div>

                    {/* <Button
                  type="outline"
                  onClick={handleCommentClick}
                  className="!md:flex hidden !items-center !gap-1 !sm:gap-[8px] !border !border-[#475467] !text-[#475467] !bg-transparent !sm:px-[17px] !sm:py-[10px] !py-1.5 !px-32!h-fit !rounded-[5px] !sm:text-[14px] !text-xs !hover:bg-transparent"
                >
                  Comments (0)
                </Button> */}
                  </div>
                </div>

                {/* Tabs and Content */}
                <div className={styles.body}>
                  <div className={`bg-white sm:rounded-tl-[12px] rounded-tl-none fixed lg:top-[-23px] top-[-16px] right-0 ${styles.tabs}`}>
                    {visibleTabs.map((tab) => (
                      <button
                        key={tab}
                        className={`py-4 pl-4 pr-3 !ml-0 flex-1 ${styles.tab} ${activeTab === tab ? `${styles.active} sm:px-6 sm:py-4 border-b border-[#012169] bg-[#D1E9FF] text-[#012169] py-2` : ''}`}
                        onClick={() => handleTabChange(tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {/* Tab Content */}
                  {activeTab === 'Summary' && (
                    <div className={styles.content}>
                      {summaryLoading ? (
                        <div className="flex justify-center items-center py-20">
                          <Spinner />
                        </div>
                      ) : !summary ? (
                        <div className="flex flex-col items-center justify-center py-[100px] px-5">
                          <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                          <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Summary Data</h3>
                          <p className="text-sm text-[#667085] text-center max-w-md">
                            Summary data is not available for this candidate yet.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div data-tour-id="tour-report-alignment" className="flex items-center gap-[3%] pl-5 pr-4 md:pt-10 pt-5 pb-5 cursor-pointer">
                            <h3 className="whitespace-nowrap text-[#012169] font-medium text-xl sm:text-2xl lg:text-[29px]">
                              Alignment Overview
                            </h3>
                            <div className="h-[1.5px] bg-[#F2F4F7] w-[67%]"></div>
                          </div>

                          <div className='mx-5 border border-[#EAECF0] rounded-lg overflow-hidden'>
                            <SummaryTable
                              tableBodyItems={(summary?.alignment_overview || []).filter((item) => {
                                const key = matchCategoryToSectionKey(item?.category);
                                // Keep rows we can't confidently map; drop only those
                                // whose metric the recruiter deleted.
                                return !key || isSectionEnabled(enabledSections, key);
                              })}
                            />
                          </div>

                          {/* Render Top Strength Highlights from API */}
                          <div className={styles.tabHeader}>
                            <h3
                              data-tour-id="tour-report-strengths"
                              className='sm:text-3xl text-2xl whitespace-nowrap font-medium text-[#012169] inline-flex w-fit items-center'
                            >
                              Top Strength Highlights
                            </h3>
                            <div className={styles.line}></div>
                          </div>
                          <div className='grid sm:grid-cols-2 gap-5 p-5'>
                            {(summary?.top_strength_highlights || []).length > 0 ? (
                              summary.top_strength_highlights.map((skill, index) => (
                                <div key={index} className='bg-[#F5FAFF] p-6 rounded-lg border border-[#D1E9FF]'>
                                  <div className='flex gap-2 items-center mb-[10px]'>
                                    <h3 className='text-[#1D2939] text-lg font-medium m-0'>{skill?.title || 'N/A'}</h3>
                                    <span
                                      className={`px-[7px] py-0.5 rounded-[10px] text-white text-[12px] font-medium ${getBadgeBgClass(skill?.badge)}`}
                                    >
                                      {skill.badge}
                                    </span>
                                  </div>
                                  <p className='text-[#475467] text-sm m-0'>{skill.description}</p>
                                </div>
                              ))
                            ) : (
                              <p className="text-[#667085] text-sm col-span-2">No strength highlights available</p>
                            )}
                          </div>

                          {/* Render Development Watchouts from API */}
                          <div data-tour-id="tour-report-watchouts" className={styles.tabHeader}>
                            <h3 className='sm:text-3xl text-2xl whitespace-nowrap font-medium text-[#012169]'>
                              Development Watchouts
                            </h3>
                            <div className={styles.line}></div>
                          </div>
                          <div className='grid sm:grid-cols-2 gap-5 p-5'>
                            {(summary?.development_watchouts || []).length > 0 ? (
                              summary.development_watchouts.map((skill, index) => (
                                <div key={index} className='bg-[#F5FAFF] p-6 rounded-lg border border-[#D1E9FF]'>
                                  <div className='flex gap-2 items-center mb-[10px]'>
                                    <h3 className='text-[#1D2939] text-lg font-medium m-0'>{skill.title}</h3>
                                    <span
                                      className={`px-[7px] py-0.5 rounded-[10px] text-white text-[12px] font-medium ${getBadgeBgClass(skill.badge)}`}
                                    >
                                      {skill.badge}
                                    </span>
                                  </div>
                                  <p className='text-[#475467] text-sm m-0'>{skill.description}</p>
                                </div>
                              ))
                            ) : (
                              <p className="text-[#667085] text-sm col-span-2">No development watchouts available</p>
                            )}
                          </div>

                          {/* Render Suggestion from API */}
                          <div data-tour-id="tour-report-suggestion" className={styles.tabHeader}>
                            <h3 className='sm:text-3xl text-2xl whitespace-nowrap font-medium text-[#012169]'>
                              Suggestion
                            </h3>
                            <div className={styles.line}></div>
                          </div>
                          {summary?.suggestion ? (
                            <div className='p-5'>
                              <div className='bg-[#F5FAFF] p-6 rounded-lg border border-[#D1E9FF] max-w-[1008px]'>
                                <div className='flex gap-2 items-center mb-4'>
                                  <h3 className='text-[#1D2939] text-[28px] font-semibold leading-none m-0'>{summary.suggestion.action}</h3>
                                  <SuggestionStatusIcon state={suggestionVisualState} />
                                </div>
                                <p className='text-[#475467] text-sm leading-8 m-0 max-w-[780px]'>{summary.suggestion.description}</p>
                              </div>
                            </div>
                          ) : (
                            <div className='p-5'>
                              <p className="text-[#667085] text-sm">No suggestion available</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'Basic Expectation' && (
                    <div className={`pb-4 ${styles.scroll}`}>
                      {basicExpectationLoading ? (
                        <div className="flex justify-center items-center py-20">
                          <Spinner />
                        </div>
                      ) : !basicExpectation ? (
                        <div className="flex flex-col items-center justify-center py-[100px] px-5">
                          <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                          <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Basic Expectation Data</h3>
                          <p className="text-sm text-[#667085] text-center max-w-md">
                            Basic expectation data is not available for this candidate yet.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className={styles.chartt}>
                            <div className={`${styles.scoreWrap} ${styles.dough}`}>
                              {basicExpectationScore !== undefined ? (
                                <>
                                  <DoughnutChart
                                    score={basicExpectationScore}
                                    cutout="60%"
                                    total={100}
                                    className={styles.chart}
                                  />
                                  <p
                                    className={`${styles.scoreCount} ${styles.centerText} ${basicExpectationScore >= 100 ? styles.threeDigit : ''} ${basicExpectationScore >= 90
                                      ? styles.green
                                      : basicExpectationScore >= 70
                                        ? styles.blue
                                        : basicExpectationScore >= 50
                                          ? styles.orange
                                          : styles.red
                                      }`}
                                  >
                                    {basicExpectationScore}%
                                  </p>
                                </>
                              ) : (
                                <div className="flex items-center justify-center h-full text-center">
                                  <p className="text-[#667085] text-sm">Score not available</p>
                                </div>
                              )}
                            </div>
                            <div className={styles.explanation}>
                              <h3>{activeTab}s</h3>
                              Conceptual thinking, issue identification, key information extraction for workable solutions.
                            </div>
                          </div>
                          {basicExpectationTableItems.length > 0 && (
                            <div className='mx-5 mb-8'>
                              <BasicExpectationsTable
                                tableBodyItems={basicExpectationTableItems}
                              />
                            </div>
                          )}


                          {/* CV Relevance Section */}
                          {showCvRelevance && (
                            <div className="mx-4 mt-6 mb-6 lg:w-[40%]">
                              <div className="bg-[#F5FAFF] rounded-lg px-6 py-4 border border-[#D1E9FF]">
                                <h3 className="text-[#012169] font-medium sm:text-lg text-base mb-1">CV Relevance</h3>
                                <div className="flex items-center gap-4 mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-medium text-[#344054]">{basicExpectation.cv_relevance.level}</span>
                                    </div>
                                    <div className='flex items-center gap-4'>
                                      <div className="w-full bg-[#E4E7EC] rounded-full h-2.5">
                                        <div
                                          className={`h-2.5 rounded-full ${getScoreBgClass(basicExpectation.cv_relevance.percentage)}`}
                                          style={{ width: `${basicExpectation.cv_relevance.percentage}%` }}
                                        ></div>
                                      </div>
                                      <span className="text-xs font-medium text-[#344054]">{basicExpectation.cv_relevance.percentage}%</span>
                                    </div>
                                  </div>
                                </div>
                                {/* <p className="text-[#475467] text-sm leading-relaxed">{basicExpectation.cv_relevance.summary}</p> */}
                              </div>
                            </div>
                          )}

                          {/* Strengths and Gaps Section */}
                          {(basicExpectation?.strengths?.length > 0 || basicExpectation?.gaps?.length > 0 || basicExpectation?.implications?.length > 0) && (
                            <>
                              {/* Real content - renders when data exists */}
                              <div className="mx-4 mb-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                  {basicExpectation?.strengths && basicExpectation.strengths.length > 0 && (
                                    <div className="bg-white rounded-lg p-6 border border-[#D1E9FF]">
                                      <h3 className="font-medium text-base mb-4 px-4 py-2 bg-[#EFF8FF] rounded-md text-[#012169] w-fit">Strengths</h3>
                                      <ul className="space-y-3">
                                        {basicExpectation.strengths.map((strength, index) => (
                                          <li key={index} className="flex gap-2 text-sm text-[#475467]">
                                            <span className="text-[#475467] mt-0.5">•</span>
                                            <span>{strength}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {basicExpectation?.gaps && basicExpectation.gaps.length > 0 && (
                                    <div className="bg-white rounded-lg p-6 border border-[#D1E9FF]">
                                      <h3 className="font-medium text-base mb-4 px-4 py-2 bg-[#FFE6E6] rounded-md text-[#B30000] w-fit">Gaps</h3>
                                      <ul className="space-y-3">
                                        {basicExpectation.gaps.map((gap, index) => (
                                          <li key={index} className="flex gap-2 text-sm text-[#475467]">
                                            <span className="text-[#475467] mt-0.5">•</span>
                                            <span>{gap}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {basicExpectation?.implications && basicExpectation.implications.length > 0 && (
                                <div className="mx-4 mb-6">
                                  <div className="bg-[#F5FAFF] rounded-lg p-6 border border-[#D1E9FF]">
                                    <h3 className="font-medium text-base mb-4 px-4 py-2 bg-[#E6F3FF] rounded-md text-[#012169] w-fit">Implications</h3>
                                    <ul className="space-y-3">
                                      {basicExpectation.implications.map((implication, index) => (
                                        <li key={index} className="flex gap-2 text-sm text-[#475467]">
                                          <span className="text-[#475467] mt-0.5">•</span>
                                          <span>{implication}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'Soft Skills' && (
                    <>
                      <div className=''>
                        {softSkillsLoading ? (
                          <div className="flex justify-center items-center py-20">
                            <Spinner />
                          </div>
                        ) : !softSkills ? (
                          <div className="flex flex-col items-center justify-center py-[100px] px-5">
                            <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                            <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Soft Skills Data</h3>
                            <p className="text-sm text-[#667085] text-center max-w-md">
                              Soft skills data is not available for this candidate yet.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className={styles.chartt}>
                              <div className={`${styles.scoreWrap} ${styles.dough}`}>
                                {softSkills?.score !== undefined ? (
                                  <>
                                    <DoughnutChart
                                      score={softSkills.score}
                                      cutout="60%"
                                      total={100}
                                      className={styles.chart}
                                    />
                                    <p
                                      className={`${styles.scoreCount} ${styles.centerText} ${softSkills.score >= 100 ? styles.threeDigit : ''} ${softSkills.score >= 90
                                        ? styles.green
                                        : softSkills.score >= 70
                                          ? styles.blue
                                          : softSkills.score >= 50
                                            ? styles.orange
                                            : styles.red
                                        }`}
                                    >
                                      {softSkills.score}%
                                    </p>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-center h-full text-center">
                                    <p className="text-[#667085] text-sm">Score not available</p>
                                  </div>
                                )}
                              </div>
                              <div className={styles.explanation}>
                                <h3>{activeTab}</h3>
                                Conceptual thinking, issue identification, key information extraction for workable solutions.
                              </div>
                            </div>
                            <div className='mx-6 pb-6'>
                              <SkillsComparisonChart
                                expectationLabel="Expectation"
                                resultLabel="Candidate's Result"
                                skills={transformSoftSkillsToChart()}
                                maxValue={100}
                                showPercentages={true}
                              />
                              <CollapsibleSection title='Analysis' skills={transformSoftSkillsToAnalysis()} />
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {activeTab === 'Work Style' && (
                    <>
                      <div className=''>
                        {workStyleLoading ? (
                          <div className="flex justify-center items-center py-20">
                            <Spinner />
                          </div>
                        ) : !workStyle ? (
                          <div className="flex flex-col items-center justify-center py-[100px] px-5">
                            <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                            <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Work Style Data</h3>
                            <p className="text-sm text-[#667085] text-center max-w-md">
                              Work style data is not available for this candidate yet.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className={styles.chartt}>
                              <div className={`${styles.scoreWrap} ${styles.dough}`}>
                                {workStyleScore !== undefined ? (
                                  <>
                                    <DoughnutChart
                                      score={workStyleScore}
                                      cutout="60%"
                                      total={100}
                                      className={styles.chart}
                                    />
                                    <p
                                      className={`${styles.scoreCount} ${styles.centerText} ${workStyleScore >= 100 ? styles.threeDigit : ''} ${workStyleScore >= 90
                                        ? styles.green
                                        : workStyleScore >= 70
                                          ? styles.blue
                                          : workStyleScore >= 50
                                            ? styles.orange
                                            : styles.red
                                        }`}
                                    >
                                      {workStyleScore}%
                                    </p>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-center h-full text-center">
                                    <p className="text-[#667085] text-sm">Score not available</p>
                                  </div>
                                )}
                              </div>
                              <div className={styles.explanation}>
                                <h3>{activeTab}</h3>
                                Conceptual thinking, issue identification, key information extraction for workable solutions.
                              </div>
                            </div>
                            <div className='mx-6 pb-6'>
                              <SkillsComparisonChart
                                expectationLabel="Expectation"
                                resultLabel="Candidate's Result"
                                skills={transformWorkStyleToChart()}
                                maxValue={100}
                                showPercentages={true}
                              />
                              <CollapsibleSection title='Analysis' skills={transformWorkStyleToAnalysis()} />
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {activeTab === 'Workplace Qualities' && (
                    <>
                      <div className=''>
                        {workplaceQualitiesLoading ? (
                          <div className="flex justify-center items-center py-20">
                            <Spinner />
                          </div>
                        ) : !workplaceQualities ? (
                          <div className="flex flex-col items-center justify-center py-[100px] px-5">
                            <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                            <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Workplace Qualities Data</h3>
                            <p className="text-sm text-[#667085] text-center max-w-md">
                              Workplace qualities data is not available for this candidate yet.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className={styles.chartt}>
                              <div className={`${styles.scoreWrap} ${styles.dough}`}>
                                {workplaceQualitiesScore !== undefined ? (
                                  <>
                                    <DoughnutChart
                                      score={workplaceQualitiesScore}
                                      cutout="60%"
                                      total={100}
                                      className={styles.chart}
                                    />
                                    <p
                                      className={`${styles.scoreCount} ${styles.centerText} ${workplaceQualitiesScore >= 100 ? styles.threeDigit : ''} ${workplaceQualitiesScore >= 90
                                        ? styles.green
                                        : workplaceQualitiesScore >= 70
                                          ? styles.blue
                                          : workplaceQualitiesScore >= 50
                                            ? styles.orange
                                            : styles.red
                                        }`}
                                    >
                                      {workplaceQualitiesScore}%
                                    </p>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-center h-full text-center">
                                    <p className="text-[#667085] text-sm">Score not available</p>
                                  </div>
                                )}
                              </div>
                              <div className={styles.explanation}>
                                <h3>{activeTab}</h3>
                                Conceptual thinking, issue identification, Workplace qualities represent a candidate's core behavioral tendencies and how they typically operate in a work environment key information extraction for workable solutions.
                              </div>
                            </div>
                            <div className='mx-6 pb-6'>
                              <SkillsComparisonChart
                                expectationLabel="Expectation"
                                resultLabel="Candidate's Result"
                                skills={transformWorkplaceQualitiesToChart()}
                                maxValue={100}
                                showPercentages={true}
                              />
                              <CollapsibleSection title='Analysis' skills={transformWorkplaceQualitiesToAnalysis()} />
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {activeTab === 'Work Motivators' && (
                    <>
                      <div className=''>
                        {workMotivatorLoading ? (
                          <div className="flex justify-center items-center py-20">
                            <Spinner />
                          </div>
                        ) : !workMotivator ? (
                          <div className="flex flex-col items-center justify-center py-[100px] px-5">
                            <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                            <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Work Motivators Data</h3>
                            <p className="text-sm text-[#667085] text-center max-w-md">
                              Work motivators data is not available for this candidate yet.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className={styles.chartt}>
                              <div className={`${styles.scoreWrap} ${styles.dough}`}>
                                {workMotivatorsScore !== undefined ? (
                                  <>
                                    <DoughnutChart
                                      score={workMotivatorsScore}
                                      cutout="60%"
                                      total={100}
                                      className={styles.chart}
                                    />
                                    <p
                                      className={`${styles.scoreCount} ${styles.centerText} ${workMotivatorsScore >= 100 ? styles.threeDigit : ''} ${workMotivatorsScore >= 90
                                        ? styles.green
                                        : workMotivatorsScore >= 70
                                          ? styles.blue
                                          : workMotivatorsScore >= 50
                                            ? styles.orange
                                            : styles.red
                                        }`}
                                    >
                                      {workMotivatorsScore}%
                                    </p>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-center h-full text-center">
                                    <p className="text-[#667085] text-sm">Score not available</p>
                                  </div>
                                )}
                              </div>
                              <div className={styles.explanation}>
                                <h3>{activeTab}</h3>
                                Work motivators represent the key drivers that influence how a candidate approaches their work, what excites them, and how they are motivated to achieve results. These motivators reflect what keeps them engaged and fulfilled in a role.
                              </div>
                            </div>
                            <div className='mx-6 pb-6'>
                              <SkillsComparisonChart
                                expectationLabel="Expectation"
                                resultLabel="Candidate's Result"
                                skills={transformWorkMotivatorsToChart()}
                                maxValue={100}
                                showPercentages={true}
                              />
                              {workMotivatorsAnalysis.length > 0 && (
                                <CollapsibleSection title='Analysis' skills={workMotivatorsAnalysis} />
                              )}
                              {otherKeyMotivatorsAnalysis.length > 0 && (
                                <CollapsibleSection title='Other Key Motivators' skills={otherKeyMotivatorsAnalysis} />
                              )}
                              {/* {employerExpectationsAnalysis.length > 0 && (
                                <CollapsibleSection title='Employer Expectations' skills={employerExpectationsAnalysis} />
                              )} */}
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {activeTab === 'Personality' && (
                    <>
                      <div className=''>
                        {personalityLoading ? (
                          <div className="flex justify-center items-center py-20">
                            <Spinner />
                          </div>
                        ) : !personality ? (
                          <div className="flex flex-col items-center justify-center py-[100px] px-5">
                            <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                            <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Personality Data</h3>
                            <p className="text-sm text-[#667085] text-center max-w-md">
                              Personality data is not available for this candidate yet.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className={styles.chartt}>
                              <div className={`${styles.scoreWrap} ${styles.dough}`}>
                                {personalityScore !== undefined ? (
                                  <>
                                    <DoughnutChart
                                      score={personalityScore}
                                      cutout="60%"
                                      total={100}
                                      className={styles.chart}
                                    />
                                    <p
                                      className={`${styles.scoreCount} ${styles.centerText} ${personalityScore >= 100 ? styles.threeDigit : ''} ${personalityScore >= 90
                                        ? styles.green
                                        : personalityScore >= 70
                                          ? styles.blue
                                          : personalityScore >= 50
                                            ? styles.orange
                                            : styles.red
                                        }`}
                                    >
                                      {personalityScore}%
                                    </p>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-center h-full text-center">
                                    <p className="text-[#667085] text-sm">Score not available</p>
                                  </div>
                                )}
                              </div>
                              <div className={styles.explanation}>
                                <h3>{activeTab}</h3>
                                Personality reflects how a candidate naturally approaches work, collaborates with others, processes information, solves problems, and makes decisions.
                                <PersonalityAlignmentBadge score={personalityScore} className='mt-3' />
                              </div>
                            </div>
                            <div className='mx-4 pb-6'>
                              <PersonalityAlignmentChart
                                coreAreas={personality.core_areas}
                                employerExpectations={personality.employer_expectations}
                                className='mt-7'
                              />
                              {/* Personality Type Section */}
                              <div className='py-10'>
                                <div className='grid md:grid-cols-2 gap-6'>
                                  {/* Employer Preference */}
                                  {personality.employer_preference && (
                                    <div>
                                      <h4 className='text-xl pb-2 font-semibold text-[#6B7280]'>Preference</h4>
                                      <div className='border border-[#D1E9FF] rounded-lg overflow-hidden shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1)] h-full'>
                                        <div className='p-4'>
                                          <h5 className='text-[#012169] font-semibold md:text-base mb-4 px-3 py-2 bg-[#EFF8FF] rounded-md w-fit'>
                                            {personality.employer_preference.personality_type}
                                          </h5>
                                          <div className='mb-3'>
                                            <h6 className='text-sm font-semibold text-[#1D2939] mb-2'>Strengths</h6>
                                            <ul className='list-disc pl-5 space-y-1'>
                                              {(personality.employer_preference.strengths || []).map((s, idx) => (
                                                <li key={idx} className='text-sm text-[#475467]'>
                                                  <span className='font-medium'>{s.title}:</span> {s.content}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                          <div>
                                            <h6 className='text-sm font-semibold text-[#1D2939] mb-2'>Areas of Growth</h6>
                                            <ul className='list-disc pl-5 space-y-1'>
                                              {(personality.employer_preference.growth_areas || []).map((a, idx) => (
                                                <li key={idx} className='text-sm text-[#475467]'>
                                                  <span className='font-medium'>{a.title}:</span> {a.content}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {/* Candidate Personality Type */}
                                  <div>
                                    <h4 className='text-xl pb-2 font-semibold text-[#6B7280]'>Candidate Personality Type</h4>
                                    <div className='border border-[#D1E9FF] rounded-lg overflow-hidden shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1)] h-full'>
                                      <div className='p-4'>
                                        <h5 className='text-[#012169] font-semibold md:text-base mb-4 px-3 py-2 bg-[#EFF8FF] rounded-md w-fit'>
                                          {personality.personality_type || 'N/A'}
                                        </h5>
                                        {personality.description && (
                                          <p className='text-sm text-[#475467] mb-4'>{personality.description}</p>
                                        )}
                                        <div className='mb-3'>
                                          <h6 className='text-sm font-semibold text-[#1D2939] mb-2'>Strengths</h6>
                                          <ul className='list-disc pl-5 space-y-1'>
                                            {Array.isArray(personality?.strengths) && personality.strengths.map((strength, idx) => (
                                              <li key={idx} className='text-sm text-[#475467]'>
                                                <span className='font-medium'>{strength.title}:</span> {strength.content}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                        <div>
                                          <h6 className='text-sm font-semibold text-[#1D2939] mb-2'>Areas of Growth</h6>
                                          <ul className='list-disc pl-5 space-y-1'>
                                            {Array.isArray(personality?.growth_areas) && personality.growth_areas.map((area, idx) => (
                                              <li key={idx} className='text-sm text-[#475467]'>
                                                <span className='font-medium'>{area.title}:</span> {area.content}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Collapsible Analysis Section */}
                              <CollapsibleSection title='Analysis' skills={transformPersonalityToAnalysis()} />
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {activeTab === 'Domain Knowledge' && (
                    <>
                      <div className=''>
                        {domainKnowledgeLoading ? (
                          <div className="flex justify-center items-center py-20">
                            <Spinner />
                          </div>
                        ) : !(domainKnowledge?.results && Array.isArray(domainKnowledge.results) && domainKnowledge.results.length > 0) ? (
                          <div className="flex flex-col items-center justify-center py-[100px] px-5">
                            <img src={emptyReport2} alt="No responses recorded" className="w-48 h-40 mb-4 opacity-80" />
                            <h3 className="text-lg font-medium text-[#1D2939] mb-2">No Domain Knowledge Data</h3>
                            <p className="text-sm text-[#667085] text-center max-w-md">
                              Domain knowledge assessment data is not available for this candidate yet.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className={styles.chartt}>
                              <div className={`${styles.scoreWrap} ${styles.dough}`}>
                                {domainKnowledgeScore !== undefined ? (
                                  <>
                                    <DoughnutChart
                                      score={domainKnowledgeScore}
                                      cutout="60%"
                                      total={100}
                                      className={styles.chart}
                                    />
                                    <p
                                      className={`${styles.scoreCount} ${styles.centerText} ${domainKnowledgeScore >= 100 ? styles.threeDigit : ''} ${domainKnowledgeScore >= 90
                                        ? styles.green
                                        : domainKnowledgeScore >= 70
                                          ? styles.blue
                                          : domainKnowledgeScore >= 50
                                            ? styles.orange
                                            : styles.red
                                        }`}
                                    >
                                      {domainKnowledgeScore}%
                                    </p>
                                  </>
                                ) : (
                                  <div className="flex items-center justify-center h-full text-center">
                                    <p className="text-[#667085] text-sm">Score not available</p>
                                  </div>
                                )}
                              </div>
                              <div className={styles.explanation}>
                                <h3>{activeTab}</h3>
                                Assessment of technical knowledge and domain-specific expertise.
                              </div>
                            </div>
                            <DomainKnowledgeSection />
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      <Modal
        position="centered"
        show={showRetakeModal}
        close={() => !isRetakeLoading && setShowRetakeModal(false)}
        contentClassName="w-[90%] !max-w-[518px] p-6 rounded-[12px] text-center relative"
      >
        <button
          type="button"
          aria-label="Close"
          className="absolute top-3 right-3 bg-transparent text-[#475467] border-none cursor-pointer p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => !isRetakeLoading && setShowRetakeModal(false)}
          disabled={isRetakeLoading}
        >
          <CloseCircleGreyIcon className='text-[#475467]'/>
        </button>
        <div className="w-20 h-20 rounded-[12px] bg-[#FFF5E6] text-[#DB8400] flex items-center justify-center mx-auto mt-8 mb-6">
          <svg width="46" height="49" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M 8.7 4.2 L 15.3 4.2 Q 16.5 4.2 17.1 5.24 L 20.4 10.96 Q 21 12 20.4 13.04 L 17.1 18.76 Q 16.5 19.8 15.3 19.8 L 8.7 19.8 Q 7.5 19.8 6.9 18.76 L 3.6 13.04 Q 3 12 3.6 10.96 L 6.9 5.24 Q 7.5 4.2 8.7 4.2 Z" />
            <line x1="12" y1="9" x2="12" y2="12.5" />
            <line x1="12" y1="15" x2="12.01" y2="15" />
          </svg>
        </div>
        <h3 className="text-[22px] font-semibold text-[#101828] mb-2">Allow candidate to retake assessment?</h3>
        <p className="text-base text-[#475467] leading-[1.5] mb-6 lg:w-[75%] mx-auto">
          A new assessment link will be sent to the candidate. This current report will be removed and
          replaced after the new attempt.
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            type="outline"
            onClick={() => setShowRetakeModal(false)}
            disabled={isRetakeLoading}
            className="!border !border-[#D0D5DD] !px-6 !w-fit min-w-fit !text-[#344054] !bg-transparent !rounded-[8px] !h-11"
          >
            Cancel
          </Button>
          <Button
            type="fill"
            onClick={handleAllowRetake}
            disabled={isRetakeLoading}
            className="!bg-[#012169] !px-6 !w-fit min-w-fit !border-none !text-white !rounded-[8px] !h-11"
          >
            {isRetakeLoading ? 'Processing...' : 'Allow Retake'}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export { AssessmentReportUI }
